import { useState, useEffect } from 'react';
import { 
  Modal, 
  Steps, 
  Form, 
  DatePicker, 
  Input, 
  Button, 
  Space, 
  Table, 
  InputNumber,
  App,
  Divider,
  Alert,
  Checkbox,
  Card,
} from 'antd';
import { PlusOutlined, DeleteOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import type { 
  EventWithCircuit, 
  UpdateEventRequest, 
  Station, 
  Session, 
  RadarPeriod,
  RadarBbox 
} from '../../api/eventsService';
import eventsService from '../../api/eventsService';
import { useAuth } from '../../hooks/useAuth';
import { DateTimeInput } from '../Common/DateTimeInput';
import { useEvent } from '../../hooks/useEvent';

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);

const { TextArea } = Input;
const { RangePicker } = DatePicker;

interface UpdateEventModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  event: EventWithCircuit | null;
}

interface StationTableRecord extends Station {
  key: string;
}

interface SessionTableRecord extends Session {
  key: string;
}

interface RadarPeriodTableRecord extends RadarPeriod {
  key: string;
}

// Simple bbox coordinates for UI (min/max lat/lon)
interface BboxCoordinates {
  minLon: number;
  minLat: number;
  maxLon: number;
  maxLat: number;
}

// Convert GeoJSON Polygon to simple bbox coordinates
const bboxToCoordinates = (bbox: RadarBbox | null | undefined): BboxCoordinates | null => {
  if (!bbox || !bbox.coordinates || !bbox.coordinates[0] || bbox.coordinates[0].length < 4) {
    return null;
  }
  
  const coords = bbox.coordinates[0];
  // Extract min/max from the polygon coordinates
  const lons = coords.map(c => c[0]);
  const lats = coords.map(c => c[1]);
  
  return {
    minLon: Math.min(...lons),
    maxLon: Math.max(...lons),
    minLat: Math.min(...lats),
    maxLat: Math.max(...lats),
  };
};

// Convert simple bbox coordinates to GeoJSON Polygon
const coordinatesToBbox = (coords: BboxCoordinates): RadarBbox => {
  return {
    type: 'Polygon',
    coordinates: [[
      [coords.minLon, coords.minLat],
      [coords.maxLon, coords.minLat],
      [coords.maxLon, coords.maxLat],
      [coords.minLon, coords.maxLat],
      [coords.minLon, coords.minLat], // Close the polygon
    ]],
  };
};

export const UpdateEventModal: React.FC<UpdateEventModalProps> = ({
  open,
  onClose,
  onSuccess,
  event,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { authState } = useAuth();
  const { message } = App.useApp();
  const { selectedEvent, setSelectedEvent } = useEvent();
  
  // Form state
  const [circuitId, setCircuitId] = useState<string>('');
  const [circuitGeojsonId, setCircuitGeojsonId] = useState<string>('');
  const [eventName, setEventName] = useState<string>('');
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [overviewSummary, setOverviewSummary] = useState<string>('');
  const [isDemo, setIsDemo] = useState<boolean>(false);
  const [observationsEnabled, setObservationsEnabled] = useState<boolean>(true);
  const [stations, setStations] = useState<StationTableRecord[]>([]);
  const [sessions, setSessions] = useState<SessionTableRecord[]>([]);
  const [radarPeriods, setRadarPeriods] = useState<RadarPeriodTableRecord[]>([]);
  const [radarBbox, setRadarBbox] = useState<BboxCoordinates | null>(null);
  

  // Initialize form with event data
  useEffect(() => {
    if (open && event) {
      setCircuitId(event.circuit_id);
      setCircuitGeojsonId(event.circuit_geojson_id || '');
      setEventName(event.name);
      setDateRange([dayjs(event.start_date), dayjs(event.end_date)]);
      setOverviewSummary(event.overview_summary || '');
      setIsDemo(event.demo);
      setObservationsEnabled(event.observations_enabled ?? true);
      
      // Populate stations
      const stationRecords: StationTableRecord[] = event.stations.map((s, idx) => ({
        ...s,
        key: `station-${idx}`,
      }));
      setStations(stationRecords);
      
      // Populate sessions
      const sessionRecords: SessionTableRecord[] = event.sessions.map((s, idx) => ({
        ...s,
        key: `session-${idx}`,
      }));
      setSessions(sessionRecords);
      
      // Populate radar periods
      if (event.radar_periods && event.radar_periods.length > 0) {
        const radarRecords: RadarPeriodTableRecord[] = event.radar_periods.map((p, idx) => ({
          ...p,
          key: `radar-${idx}`,
        }));
        setRadarPeriods(radarRecords);
      } else {
        setRadarPeriods([]);
      }
      
      // Populate radar bbox
      const bboxCoords = bboxToCoordinates(event.radar_bbox);
      setRadarBbox(bboxCoords);
      
      form.setFieldsValue({
        circuitId: event.circuit_id,
        circuitGeojsonId: event.circuit_geojson_id || '',
        eventName: event.name,
        dateRange: [dayjs(event.start_date), dayjs(event.end_date)],
        overviewSummary: event.overview_summary || '',
      });
    }
  }, [open, event, form]);

  // Reset form when modal closes
  // Note: form.resetFields() is not called here because the Modal uses destroyOnHidden,
  // which destroys the Form component when closed.
  useEffect(() => {
    if (!open) {
      setCurrentStep(0);
      setCircuitId('');
      setCircuitGeojsonId('');
      setEventName('');
      setDateRange(null);
      setOverviewSummary('');
      setIsDemo(false);
      setObservationsEnabled(true);
      setStations([]);
      setSessions([]);
      setRadarPeriods([]);
      setRadarBbox(null);
    }
  }, [open]);
  

  // Helper function to convert UTC timestamp string to dayjs in circuit timezone
  // Handles multiple formats: "YYYY-MM-DD HH:mm:ss.SSSSSS UTC" and ISO 8601
  const parseUTCTimestamp = (utcTimestamp: string): Dayjs => {
    if (!utcTimestamp) {
      console.error('Empty UTC timestamp');
      return dayjs().tz(event?.circuit.time_zone || 'UTC');
    }
    
    try {
      const tz = event?.circuit.time_zone || 'UTC';
      
      // Try parsing as ISO 8601 format first (e.g., "2025-11-06T13:00:00.000Z")
      if (utcTimestamp.includes('T')) {
        const parsed = dayjs.utc(utcTimestamp);
        if (parsed.isValid()) {
          return parsed.tz(tz);
        }
      }
      
      // Try parsing the "YYYY-MM-DD HH:mm:ss.SSSSSS UTC" format
      const cleanTimestamp = utcTimestamp.replace(' UTC', '').trim();
      const [datePart, timePart] = cleanTimestamp.split(' ');
      if (datePart && timePart) {
        const isoFormat = `${datePart}T${timePart.split('.')[0]}Z`;
        const parsed = dayjs.utc(isoFormat);
        if (parsed.isValid()) {
          return parsed.tz(tz);
        }
      }
      
      // Fallback: try direct parsing
      const fallback = dayjs(utcTimestamp);
      if (fallback.isValid()) {
        return fallback.tz(tz);
      }
      
      console.error('Could not parse UTC timestamp:', utcTimestamp);
      return dayjs().tz(tz);
    } catch (error) {
      console.error('Error parsing UTC timestamp:', utcTimestamp, error);
      return dayjs().tz(event?.circuit.time_zone || 'UTC');
    }
  };

  // Helper function to format dayjs to UTC timestamp string
  const formatToUTCTimestamp = (date: Dayjs): string => {
    const utcDate = date.utc();
    const formatted = utcDate.format('YYYY-MM-DD HH:mm:ss.SSSSSS');
    return `${formatted} UTC`;
  };

  // Helper function to parse ISO timestamp to circuit timezone
  const parseISOToCircuitTZ = (isoTimestamp: string): Dayjs => {
    return dayjs(isoTimestamp).tz(event?.circuit.time_zone || 'UTC');
  };

  // Handle step navigation
  const handleNext = async () => {
    try {
      // Validate current step
      if (currentStep === 0) {
        await form.validateFields(['circuitId', 'eventName', 'dateRange']);
      }
      // Step 1 is overview summary - no validation needed
      
      setCurrentStep(currentStep + 1);
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const handlePrevious = () => {
    setCurrentStep(currentStep - 1);
  };

  // Handle update event
  const handleUpdate = async () => {
    if (!dateRange || !authState?.token || !event) {
      message.error('Missing required information');
      return;
    }
    
    try {
      setLoading(true);
      
      const [startDate, endDate] = dateRange;
      
      const updateRequest: UpdateEventRequest = {
        name: eventName,
        start_date: startDate.format('YYYY-MM-DD'),
        end_date: endDate.format('YYYY-MM-DD'),
        demo: isDemo,
        observations_enabled: observationsEnabled,
        overview_summary: overviewSummary || null,
        circuit_geojson_id: circuitGeojsonId || null,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        stations: stations.map(({ key, ...station }) => station),
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        sessions: sessions.map(({ key, ...session }) => session),
        radar_bbox: radarBbox ? coordinatesToBbox(radarBbox) : null,
        radar_periods: radarPeriods.length > 0 
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          ? radarPeriods.map(({ key, ...period }) => period) 
          : undefined,
      };
      
      const response = await eventsService.updateEvent(
        event.season_year,
        event.circuit_id,
        event.start_date,
        updateRequest,
        authState.token
      );
      
      if (response.success) {
        const eventType = isDemo ? 'demo event' : 'real event';
        message.success(`${eventType.charAt(0).toUpperCase() + eventType.slice(1)} updated successfully!`);
        
        // Refresh the global selectedEvent if it matches the updated event
        // The update API response includes the updated event data
        if (selectedEvent && 
            selectedEvent.season_year === event.season_year &&
            selectedEvent.circuit_id === event.circuit_id &&
            selectedEvent.start_date === event.start_date &&
            response.data) {
          // Update the global selectedEvent with the fresh data from the API response
          // This ensures sessions and other updated fields are immediately reflected
          setSelectedEvent(response.data);
        }
        
        onSuccess();
        onClose();
      } else {
        message.error('Failed to update event');
      }
    } catch (error) {
      console.error('Failed to update event:', error);
      message.error(error instanceof Error ? error.message : 'Failed to update event');
    } finally {
      setLoading(false);
    }
  };
  

  // Get circuit timezone
  const circuitTimezone = event?.circuit.time_zone || 'UTC';

  // Station columns
  const stationColumns: ColumnsType<StationTableRecord> = [
    {
      title: 'Reference',
      dataIndex: 'reference',
      key: 'reference',
      width: 100,
      render: (val: string, _record, index) => (
        <Input
          value={val}
          onChange={(e) => {
            const newStations = [...stations];
            newStations[index].reference = e.target.value;
            setStations(newStations);
          }}
        />
      ),
    },
    {
      title: 'Latitude',
      dataIndex: 'lat',
      key: 'lat',
      render: (val: number, _record, index) => (
        <InputNumber
          value={val}
          onChange={(value) => {
            const newStations = [...stations];
            newStations[index].lat = value || 0;
            setStations(newStations);
          }}
          step={0.000001}
          precision={6}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Longitude',
      dataIndex: 'lon',
      key: 'lon',
      render: (val: number, _record, index) => (
        <InputNumber
          value={val}
          onChange={(value) => {
            const newStations = [...stations];
            newStations[index].lon = value || 0;
            setStations(newStations);
          }}
          step={0.000001}
          precision={6}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Altitude',
      dataIndex: 'altitude',
      key: 'altitude',
      render: (val: number, _record, index) => (
        <InputNumber
          value={val}
          onChange={(value) => {
            const newStations = [...stations];
            newStations[index].altitude = value || 0;
            setStations(newStations);
          }}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Direction',
      dataIndex: 'nearest_segment_direction',
      key: 'nearest_segment_direction',
      render: (val: number, _record, index) => (
        <InputNumber
          value={val}
          onChange={(value) => {
            const newStations = [...stations];
            newStations[index].nearest_segment_direction = value || 0;
            setStations(newStations);
          }}
          min={0}
          max={359}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: '',
      key: 'action',
      width: 50,
      render: (_val, _record, index) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveStation(index)}
          title="Remove station"
        />
      ),
    },
  ];

  // Session columns
  const sessionColumns: ColumnsType<SessionTableRecord> = [
    {
      title: 'Session Name',
      dataIndex: 'session_name',
      key: 'session_name',
      width: 150,
      render: (val: string, _record, index) => (
        <Input
          value={val}
          onChange={(e) => {
            const newSessions = [...sessions];
            newSessions[index].session_name = e.target.value;
            setSessions(newSessions);
          }}
        />
      ),
    },
    {
      title: `Start Time (${circuitTimezone})`,
      dataIndex: 'start_time',
      key: 'start_time',
      width: 200,
      render: (val: string, _record, index) => (
        <DateTimeInput
          value={parseISOToCircuitTZ(val).format('YYYY-MM-DD HH:mm:ss')}
          onChange={(formatted) => {
            const parsed = dayjs.tz(formatted.replace(' ', 'T'), circuitTimezone);
            if (parsed.isValid()) {
              const newSessions = [...sessions];
              newSessions[index].start_time = parsed.toISOString();
              setSessions(newSessions);
            }
          }}
        />
      ),
    },
    {
      title: `End Time (${circuitTimezone})`,
      dataIndex: 'end_time',
      key: 'end_time',
      width: 200,
      render: (val: string, _record, index) => (
        <DateTimeInput
          value={parseISOToCircuitTZ(val).format('YYYY-MM-DD HH:mm:ss')}
          onChange={(formatted) => {
            const parsed = dayjs.tz(formatted.replace(' ', 'T'), circuitTimezone);
            if (parsed.isValid()) {
              const newSessions = [...sessions];
              newSessions[index].end_time = parsed.toISOString();
              setSessions(newSessions);
            }
          }}
        />
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 200,
      render: (val: string | null, _record, index) => (
        <Input
          value={val || ''}
          onChange={(e) => {
            const newSessions = [...sessions];
            newSessions[index].description = e.target.value || null;
            setSessions(newSessions);
          }}
        />
      ),
    },
    {
      title: '',
      key: 'action',
      width: 50,
      render: (_val, _record, index) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveSession(index)}
          title="Remove session"
        />
      ),
    },
  ];

  // Handler functions for adding/removing rows
  const handleAddStation = () => {
    const newStation: StationTableRecord = {
      reference: `${stations.length + 1}`,
      lat: 0,
      lon: 0,
      altitude: 0,
      nearest_segment_direction: 0,
      key: `station-${Date.now()}`,
    };
    setStations([...stations, newStation]);
  };

  const handleRemoveStation = (index: number) => {
    const newStations = stations.filter((_, i) => i !== index);
    setStations(newStations);
  };

  const handleAddSession = () => {
    const now = dayjs().tz(circuitTimezone);
    const newSession: SessionTableRecord = {
      session_name: 'NEW SESSION',
      start_time: now.toISOString(),
      end_time: now.add(1, 'hour').toISOString(),
      description: null,
      key: `session-${Date.now()}`,
    };
    setSessions([...sessions, newSession]);
  };

  const handleRemoveSession = (index: number) => {
    const newSessions = sessions.filter((_, i) => i !== index);
    setSessions(newSessions);
  };

  const handleShiftSessions = (days: number) => {
    const shifted = sessions.map(session => ({
      ...session,
      start_time: dayjs(session.start_time).add(days, 'day').toISOString(),
      end_time: dayjs(session.end_time).add(days, 'day').toISOString(),
    }));
    setSessions(shifted);
  };

  const handleAddRadarPeriod = () => {
    const now = dayjs().tz(circuitTimezone);
    const newPeriod: RadarPeriodTableRecord = {
      start_time: formatToUTCTimestamp(now),
      end_time: formatToUTCTimestamp(now.add(1, 'hour')),
      key: `radar-${Date.now()}`,
    };
    setRadarPeriods([...radarPeriods, newPeriod]);
  };

  const handleRemoveRadarPeriod = (index: number) => {
    const newRadarPeriods = radarPeriods.filter((_, i) => i !== index);
    setRadarPeriods(newRadarPeriods);
  };

  // Radar period columns
  const radarPeriodColumns: ColumnsType<RadarPeriodTableRecord> = [
    {
      title: `Start Time (${circuitTimezone})`,
      dataIndex: 'start_time',
      key: 'start_time',
      width: 220,
      render: (val: string, _record, index) => (
        <DatePicker
          showTime
          value={parseUTCTimestamp(val)}
          onChange={(date) => {
            if (date) {
              const newRadarPeriods = [...radarPeriods];
              newRadarPeriods[index].start_time = formatToUTCTimestamp(date);
              setRadarPeriods(newRadarPeriods);
            }
          }}
          format="YYYY-MM-DD HH:mm:ss"
          showNow={false}
        />
      ),
    },
    {
      title: `End Time (${circuitTimezone})`,
      dataIndex: 'end_time',
      key: 'end_time',
      width: 220,
      render: (val: string, _record, index) => (
        <DatePicker
          showTime
          value={parseUTCTimestamp(val)}
          onChange={(date) => {
            if (date) {
              const newRadarPeriods = [...radarPeriods];
              newRadarPeriods[index].end_time = formatToUTCTimestamp(date);
              setRadarPeriods(newRadarPeriods);
            }
          }}
          format="YYYY-MM-DD HH:mm:ss"
          showNow={false}
        />
      ),
    },
    {
      title: '',
      key: 'action',
      width: 50,
      render: (_val, _record, index) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveRadarPeriod(index)}
          title="Remove radar period"
        />
      ),
    },
  ];

  // Steps configuration
  const steps = [
    {
      title: 'Details',
      content: 'Event info',
    },
    {
      title: 'Overview',
      content: 'Summary',
    },
    {
      title: 'Sessions',
      content: 'Event sessions',
    },
    {
      title: 'Stations',
      content: 'Weather stations',
    },
    {
      title: 'Radar',
      content: 'Radar settings',
    },
  ];

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <Form form={form} layout="vertical">
            <Alert
              title="Event Details"
              description="Update the basic details for this event. Note: Circuit ID cannot be changed."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            
            <Form.Item
              label="Circuit ID"
              name="circuitId"
            >
              <Input
                value={circuitId}
                disabled
              />
            </Form.Item>
            
            <Form.Item
              label="Circuit GeoJSON ID"
              name="circuitGeojsonId"
              tooltip="Optional: Specify a custom GeoJSON file for this event's track layout. Leave empty to use the default circuit layout."
            >
              <Input
                value={circuitGeojsonId}
                onChange={(e) => setCircuitGeojsonId(e.target.value)}
                placeholder={`Leave empty to use "${circuitId}" layout`}
              />
            </Form.Item>
            
            <Form.Item
              label="Event Name"
              name="eventName"
              rules={[{ required: true, message: 'Please enter event name' }]}
            >
              <Input
                value={eventName}
                onChange={(e) => setEventName(e.target.value)}
              />
            </Form.Item>
            
            <Form.Item
              label="Event Date Range"
              name="dateRange"
              rules={[{ required: true, message: 'Please select date range' }]}
            >
              <RangePicker
                value={dateRange}
                onChange={(dates) => {
                  if (dates && dates[0] && dates[1]) {
                    setDateRange([dates[0], dates[1]]);
                  } else {
                    setDateRange(null);
                  }
                }}
                format="YYYY-MM-DD"
                style={{ width: '100%' }}
              />
            </Form.Item>
            
            <Form.Item>
              <Checkbox
                checked={isDemo}
                onChange={(e) => setIsDemo(e.target.checked)}
              >
                <Space orientation="vertical" size={0}>
                  <span style={{ fontWeight: 500 }}>This is a demo event</span>
                  <span style={{ fontSize: '12px', color: '#666' }}>
                    Demo events use separate tables and can be easily cleaned up
                  </span>
                </Space>
              </Checkbox>
            </Form.Item>
            
            <Form.Item>
              <Checkbox
                checked={observationsEnabled}
                onChange={(e) => setObservationsEnabled(e.target.checked)}
              >
                <Space orientation="vertical" size={0}>
                  <span style={{ fontWeight: 500 }}>Enable observations</span>
                  <span style={{ fontSize: '12px', color: '#666' }}>
                    When disabled, only forecasting features (tomorrow.io) will be available. Radar and weather station observations will be hidden.
                  </span>
                </Space>
              </Checkbox>
            </Form.Item>
          </Form>
        );
      
      case 1:
        return (
          <Form form={form} layout="vertical">
            <Alert
              title="Overview Summary"
              description="Update the event overview and summary information."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            
            <Form.Item
              label="Overview Summary"
              name="overviewSummary"
            >
              <TextArea
                value={overviewSummary}
                onChange={(e) => setOverviewSummary(e.target.value)}
                rows={12}
                placeholder="Event overview and summary information..."
                style={{ fontSize: '14px' }}
              />
            </Form.Item>
          </Form>
        );
      
      case 2:
        return (
          <div>
            <Alert
              title="Event Sessions"
              description={`Update or add event sessions. Times are displayed in ${circuitTimezone}.`}
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <Space style={{ marginBottom: 12 }}>
              <Button
                icon={<LeftOutlined />}
                onClick={() => handleShiftSessions(-1)}
                disabled={sessions.length === 0}
              >
                1 Day Back
              </Button>
              <Button
                icon={<RightOutlined />}
                onClick={() => handleShiftSessions(1)}
                disabled={sessions.length === 0}
              >
                1 Day Forward
              </Button>
            </Space>
            <Table
              dataSource={sessions}
              columns={sessionColumns}
              pagination={false}
              scroll={{ x: 900 }}
              size="small"
            />
            <Button
              type="dashed"
              onClick={handleAddSession}
              style={{ width: '100%', marginTop: 16 }}
              icon={<PlusOutlined />}
            />
          </div>
        );
      
      case 3:
        return (
          <div>
            <Alert
              title="Weather Stations"
              description="Update or add weather station locations for this event."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <Table
              dataSource={stations}
              columns={stationColumns}
              pagination={false}
              scroll={{ x: 850 }}
              size="small"
            />
            <Button
              type="dashed"
              onClick={handleAddStation}
              style={{ width: '100%', marginTop: 16 }}
              icon={<PlusOutlined />}
            />
          </div>
        );
      
      case 4:
        return (
          <div>
            <Alert
              title="Radar Settings"
              description={`Configure radar bounding box and coverage periods. Times are displayed in ${circuitTimezone} but stored as UTC.`}
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            
            <div style={{ display: 'flex', gap: 16 }}>
              {/* Radar Bounding Box - Left Card */}
              <Card size="small" title="Radar Bounding Box" style={{ width: 280, flexShrink: 0 }}>
                <div style={{ marginBottom: 8, fontSize: '12px', color: '#666' }}>
                  Define the geographic bounding box for radar data coverage.
                </div>
                <Form.Item label="Min Latitude" style={{ marginBottom: 8 }}>
                  <InputNumber
                    value={radarBbox?.minLat ?? undefined}
                    onChange={(value) => {
                      if (value !== null) {
                        setRadarBbox(prev => ({
                          minLon: prev?.minLon ?? 0,
                          maxLon: prev?.maxLon ?? 0,
                          minLat: value,
                          maxLat: prev?.maxLat ?? 0,
                        }));
                      }
                    }}
                    step={0.0001}
                    precision={6}
                    style={{ width: '100%' }}
                    placeholder="e.g. -23.7"
                  />
                </Form.Item>
                <Form.Item label="Max Latitude" style={{ marginBottom: 8 }}>
                  <InputNumber
                    value={radarBbox?.maxLat ?? undefined}
                    onChange={(value) => {
                      if (value !== null) {
                        setRadarBbox(prev => ({
                          minLon: prev?.minLon ?? 0,
                          maxLon: prev?.maxLon ?? 0,
                          minLat: prev?.minLat ?? 0,
                          maxLat: value,
                        }));
                      }
                    }}
                    step={0.0001}
                    precision={6}
                    style={{ width: '100%' }}
                    placeholder="e.g. -23.5"
                  />
                </Form.Item>
                <Form.Item label="Min Longitude" style={{ marginBottom: 8 }}>
                  <InputNumber
                    value={radarBbox?.minLon ?? undefined}
                    onChange={(value) => {
                      if (value !== null) {
                        setRadarBbox(prev => ({
                          minLon: value,
                          maxLon: prev?.maxLon ?? 0,
                          minLat: prev?.minLat ?? 0,
                          maxLat: prev?.maxLat ?? 0,
                        }));
                      }
                    }}
                    step={0.0001}
                    precision={6}
                    style={{ width: '100%' }}
                    placeholder="e.g. -46.8"
                  />
                </Form.Item>
                <Form.Item label="Max Longitude" style={{ marginBottom: 8 }}>
                  <InputNumber
                    value={radarBbox?.maxLon ?? undefined}
                    onChange={(value) => {
                      if (value !== null) {
                        setRadarBbox(prev => ({
                          minLon: prev?.minLon ?? 0,
                          maxLon: value,
                          minLat: prev?.minLat ?? 0,
                          maxLat: prev?.maxLat ?? 0,
                        }));
                      }
                    }}
                    step={0.0001}
                    precision={6}
                    style={{ width: '100%' }}
                    placeholder="e.g. -46.6"
                  />
                </Form.Item>
                {radarBbox && (
                  <Button 
                    type="link" 
                    danger 
                    size="small" 
                    onClick={() => setRadarBbox(null)}
                    style={{ padding: 0 }}
                  >
                    Clear bounding box
                  </Button>
                )}
              </Card>
              
              {/* Radar Coverage Periods - Right Card */}
              <Card size="small" title="Radar Coverage Periods" style={{ flex: 1 }}>
                <Table
                  dataSource={radarPeriods}
                  columns={radarPeriodColumns}
                  pagination={false}
                  scroll={{ x: 490 }}
                  size="small"
                  locale={{ emptyText: 'No radar periods yet. Click the + button below to create one.' }}
                />
                <Button
                  type="dashed"
                  onClick={handleAddRadarPeriod}
                  style={{ width: '100%', marginTop: 16 }}
                  icon={<PlusOutlined />}
                />
              </Card>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  if (!event) return null;

  // Don't allow updating legacy events
  if (event.legacy) {
    return (
      <Modal
        title="Cannot Update Legacy Event"
        open={open}
        onCancel={onClose}
        footer={[
          <Button key="close" onClick={onClose}>
            Close
          </Button>
        ]}
      >
        <Alert
          title="Legacy events cannot be updated"
          description="This is a legacy event and cannot be modified through this interface. Only demo and real events can be updated."
          type="warning"
          showIcon
        />
      </Modal>
    );
  }

  return (
    <Modal
      title={`Update Event: ${event.name}`}
      open={open}
      onCancel={onClose}
      width={1200}
      footer={null}
      destroyOnHidden
      maskClosable={false}
      keyboard={false}
      centered
      styles={{
        body: {
          height: 760,
          padding: '24px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Steps current={currentStep} items={steps} style={{ marginBottom: 24, flexShrink: 0 }} />
      
      <div style={{ 
        flex: 1, 
        overflowY: 'auto', 
        overflowX: 'hidden',
        paddingRight: 8,
        marginRight: -8,
      }}>
        {renderStepContent()}
      </div>
      
      <Divider style={{ margin: '16px 0', flexShrink: 0 }} />
      
      <Space style={{ width: '100%', justifyContent: 'space-between', flexShrink: 0 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Space>
          {currentStep > 0 && (
            <Button onClick={handlePrevious}>Previous</Button>
          )}
          {currentStep < steps.length - 1 && (
            <Button type="primary" onClick={handleNext}>
              Next
            </Button>
          )}
          {currentStep === steps.length - 1 && (
            <Button type="primary" onClick={handleUpdate} loading={loading}>
              Update Event
            </Button>
          )}
        </Space>
      </Space>
    </Modal>
  );
};

